const VERIFICATION_STORAGE_KEY = "verification_exception_state";

const DEFAULT_CHECKS = {
  two_factor: "Enable two-factor authentication",
  company_email: "Verify company email",
  identity: "Complete identity verification",
  kyb_document: "Upload business verification document",
  manual_review: "Manual review by compliance team",
};

const toCheckList = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value as string];
};

interface AxiosLikeError {
  response?: { status?: number; data?: Record<string, any> };
  message?: string;
}

export const extractVerificationException = (error: AxiosLikeError) => {
  const status = error?.response?.status;
  const payload = error?.response?.data || {};
  const message = payload?.message || error?.message || "Verification is required before continuing.";

  const verification =
    payload?.verification ||
    payload?.exception ||
    payload?.data?.verification ||
    payload?.data?.exception ||
    {};

  const requiredChecks = [
    ...toCheckList(verification?.requiredChecks),
    ...toCheckList(payload?.requiredChecks),
    ...toCheckList(verification?.checks),
    ...toCheckList(payload?.checks),
  ];

  const triggerReason =
    verification?.triggerReason ||
    payload?.triggerReason ||
    verification?.reason ||
    payload?.reason ||
    verification?.code ||
    payload?.code ||
    null;

  const lowerMessage = String(message).toLowerCase();
  const inferredChecks = [...requiredChecks];

  if (/2fa|two.factor/.test(lowerMessage)) inferredChecks.push("two_factor");
  if (/company email/.test(lowerMessage)) inferredChecks.push("company_email");
  if (/identity|kyc/.test(lowerMessage)) inferredChecks.push("identity");
  if (/document|kyb/.test(lowerMessage)) inferredChecks.push("kyb_document");
  if (/manual review|compliance/.test(lowerMessage)) inferredChecks.push("manual_review");

  const uniqueChecks = Array.from(new Set(inferredChecks.filter(Boolean)));
  const isVerificationError = status === 403 || Boolean(triggerReason) || uniqueChecks.length > 0;

  if (!isVerificationError) return null;

  return {
    triggerReason: triggerReason || "verification_required",
    requiredChecks: uniqueChecks,
    message,
    status,
    code: verification?.code || payload?.code || null,
    timestamp: new Date().toISOString(),
  };
};

export const persistVerificationException = (exception: Record<string, unknown> | null | undefined, context: Record<string, unknown> = {}) => {
  if (!exception) return;
  localStorage.setItem(
    VERIFICATION_STORAGE_KEY,
    JSON.stringify({
      ...exception,
      context,
    }),
  );
};

export const readVerificationException = () => {
  const raw = localStorage.getItem(VERIFICATION_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const clearVerificationException = () => {
  localStorage.removeItem(VERIFICATION_STORAGE_KEY);
};

export const formatRequiredCheck = (check?: string | null) => {
  if (!check) return "Additional verification";
  return (DEFAULT_CHECKS as Record<string, string>)[check] || String(check).replace(/[_-]/g, " ");
};
